<template>
    <div class="main-content">
        <!-- TOASTS DE NOTIFICAÇÃO -->
        <div class="toast-container position-fixed top-0 end-0 p-3" style="z-index: 1060">
            <transition-group name="slide-fade">
                <div v-for="alert in alerts" :key="alert.id"
                    class="toast show align-items-center border-0 shadow-lg mb-2"
                    :class="'bg-' + alert.type + '-subtle text-' + alert.type" role="alert">
                    <div class="d-flex p-3">
                        <i :class="alert.icon" class="me-3 fs-5"></i>
                        <div class="toast-body p-0 fw-medium">
                            {{ alert.message }}
                        </div>
                        <button type="button" class="btn-close ms-auto" @click="removeAlert(alert.id)"></button>
                    </div>
                </div>
            </transition-group>
        </div>

        <header class="content-header mb-2 mt-n5">
            <nav aria-label="breadcrumb">
                <ol class="breadcrumb mb-0 flex-nowrap text-nowrap align-items-center"
                    style="overflow-x: auto; scrollbar-width: none;">
                    <li class="breadcrumb-item">
                        <router-link class="nav-link d-inline" to="/dashboard">Início</router-link>
                    </li>
                    <li class="breadcrumb-item active text-dark small" aria-current="page">
                        Landing Pages
                    </li>
                </ol>
            </nav>
        </header>

        <div class="card p-4 shadow-subtle rounded-4 border-0">
            <div class="d-flex justify-content-between align-items-center mb-4 pb-2 border-bottom">
                <div>
                    <h4 class="mb-0 fw-bold">Landing Pages</h4>
                    <span class="text-muted fs-7">Exibindo {{ pages.length }} de {{ totalItems }}</span>
                </div>

                <div class="d-flex gap-2 align-items-center">
                    <transition name="fade">
                        <div v-if="selectedIds.length > 0" class="d-flex align-items-center me-2 pe-3 border-end">
                           <button v-if="selectedIds.length > 0" 
    class="btn btn-outline-danger btn-sm rounded-pill px-3 fw-medium d-inline-flex align-items-center gap-1"
    @click="confirmBulkDelete">
    <i class="bi bi-trash"></i> Excluir ({{ selectedIds.length }})
</button>
                        </div>
                    </transition>

                    <button class="btn btn-pmg-light" @click="showFilter = !showFilter"
                        :class="{ 'active': showFilter }">
                        <i class="bi bi-funnel"></i> Filtrar
                    </button>

                    <router-link to="/dashboard/new-page" class="btn btn-pmg-primary text-decoration-none">
                        <i class="bi bi-plus-lg me-1"></i> Nova Página
                    </router-link>
                </div>
            </div>

            <!-- Filtros -->
            <div class="collapse" :class="{ 'show': showFilter }">
                <div class="card card-body mb-4 border-0 bg-light rounded-3">
                    <div class="row g-3">
                        <div class="col-md-10">
                            <input v-model="filter.title" type="text" class="form-control form-control-pmg"
                                placeholder="Filtrar pelo título da página...">
                        </div>
                        <div class="col-md-2 d-flex">
                            <button class="btn btn-dark w-100 btn-pmg-dark fw-bold" @click="applyFilter">
                                <i class="bi bi-search me-1"></i> Filtrar
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Tabela de Listagem -->
            <div class="table-responsive">
                <table class="table table-pmg align-middle">
                    <thead>
                        <tr>
                            <th style="width: 40px;">
                                <input type="checkbox" class="form-check-input" @change="toggleAll"
                                    :checked="isAllSelected">
                            </th>
                            <th style="width: 35%; min-width: 250px;">Página / Data</th>
                            <th style="width: 20%; min-width: 180px;">Link Público</th>
                            <th style="width: 20%; min-width: 170px;">Métricas (Doações)</th>
                            <th style="width: 15%; min-width: 100px;">Status</th>
                            <th style="width: 10%; min-width: 80px;" class="text-end">Ações</th>
                        </tr>
                    </thead>

                    <tbody v-if="pages.length > 0">
                        <tr v-for="item in pages" :key="item.id"
                            :class="{ 'selected-row': selectedIds.includes(item.id) }">
                            <!-- CHECKBOX -->
                            <td>
                                <input type="checkbox" class="form-check-input" :value="item.id" v-model="selectedIds">
                            </td>

                            <!-- PÁGINA E DATA -->
                            <td>
                                <div class="fw-bold text-dark mb-1 text-wrap d-block" style="max-width: 300px;">
                                    {{ item.title }}
                                </div>
                                <div class="text-muted text-nowrap" style="font-size: 0.75rem;">
                                    <i class="bi bi-calendar3 me-1"></i> Criada em: {{ item.created_at_br || 'N/A' }}
                                </div>
                            </td>

                            <!-- LINK PÚBLICO -->
                            <td>
                                <a :href="getPublicUrl(item.slug)" target="_blank"
                                    class="text-decoration-none text-primary small fw-medium text-nowrap">
                                    <i class="bi bi-box-arrow-up-right me-1"></i> /lp/{{ item.slug }}
                                </a>
                            </td>

                            <!-- MÉTRICAS FINANCEIRAS -->
                            <td>
                                <div v-if="item.financial_stats" class="d-flex flex-column gap-1">
                                    <span
                                        class="badge bg-success-subtle text-success border border-success-subtle px-2 py-1 text-start text-nowrap"
                                        style="width: fit-content;">
                                        <i class="bi bi-check-circle-fill me-1"></i> Pago: R$ {{
                                            formatCurrency(item.financial_stats.total_paid) }}
                                    </span>
                                    <span
                                        class="badge bg-warning-subtle text-warning border border-warning-subtle px-2 py-1 text-start text-nowrap"
                                        style="width: fit-content;">
                                        <i class="bi bi-clock-history me-1"></i> Pendente: R$ {{
                                            formatCurrency(item.financial_stats.total_pending) }}
                                    </span>
                                </div>
                                <div v-else class="text-muted small text-nowrap">
                                    Sem doações
                                </div>
                            </td>

                            <!-- STATUS -->
                            <td>
                                <span class="badge rounded-pill text-nowrap"
                                    :class="item.is_active ? 'text-bg-success' : 'text-bg-secondary'">
                                    {{ item.is_active ? 'Ativa' : 'Inativa' }}
                                </span>
                            </td>

                            <!-- AÇÕES -->
                            <td class="text-end align-middle">
                                <div class="dropdown">
                                    <button
                                        class="btn btn-action-dropdown rounded-3 border bg-white shadow-sm px-2 py-1"
                                        type="button" data-bs-toggle="dropdown"
                                        data-bs-popper-config='{"strategy":"fixed"}' aria-expanded="false">
                                        <i class="bi bi-three-dots-vertical text-dark fs-6"></i>
                                    </button>
                                    <ul class="dropdown-menu dropdown-menu-end border-0 shadow-lg p-2 rounded-4">
                                        <li>
                                            <a class="dropdown-item rounded-2 py-2 d-flex align-items-center gap-2 text-secondary fw-medium"
                                                href="#" @click.prevent="openQrModal(item)">
                                                <i class="bi bi-qr-code fs-6"></i> Ver QR Code
                                            </a>
                                        </li>
                                        <li>
                                            <a class="dropdown-item rounded-2 py-2 d-flex align-items-center gap-2 text-secondary fw-medium"
                                                href="#" @click.prevent="editPage(item)">
                                                <i class="bi bi-pencil fs-6"></i> Editar
                                            </a>
                                        </li>
                                        <li>
                                            <hr class="dropdown-divider my-1 border-light">
                                        </li>
                                        <li>
                                            <a class="dropdown-item rounded-2 py-2 d-flex align-items-center gap-2 text-danger fw-medium"
                                                href="#" @click.prevent="confirmDeletePage(item)">
                                                <i class="bi bi-trash fs-6"></i> Excluir
                                            </a>
                                        </li>
                                    </ul>
                                </div>
                            </td>
                        </tr>
                    </tbody>

                    <!-- ESTADO VAZIO (6 colunas) -->
                    <tbody v-else>
                        <tr>
                            <td colspan="6" class="text-center py-5 text-muted">
                                <div class="d-flex flex-column align-items-center justify-content-center">
                                    <i class="bi bi-window-stack fs-1 mb-2 text-secondary opacity-50"></i>
                                    <span class="fw-medium">Nenhuma Landing Page encontrada.</span>
                                    <span class="small mt-1" v-if="filter.title">Tente limpar os filtros de
                                        busca.</span>
                                </div>
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>

            <!-- Botão Carregar Mais -->
            <div class="text-center mt-4" v-if="hasMore">
                <button
                    class="btn btn-pmg-light px-5 btn-sm fw-bold d-inline-flex align-items-center justify-content-center"
                    @click="loadMore" :disabled="loading" style="min-width: 220px;">
                    <span v-if="loading" class="spinner-border spinner-border-sm me-2" role="status"
                        aria-hidden="true"></span>
                    <span>{{ loading ? 'Carregando dados...' : 'Carregar mais itens' }}</span>
                </button>
            </div>
        </div>

        <!-- Modal QR Code Puro -->
        <div class="modal fade" id="qrCodeModal" tabindex="-1" aria-hidden="true">
            <div class="modal-dialog modal-dialog-centered modal-sm">
                <div class="modal-content border-0 shadow-lg rounded-4 text-center p-4">
                    <div class="modal-header border-0 pb-0 justify-content-end">
                        <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
                    </div>
                    <div class="modal-body pt-0">
                        <div class="mb-2" style="color: #561569;">
                            <i class="bi bi-qr-code-scan fs-1"></i>
                        </div>
                        <h5 class="fw-bold text-dark mb-1">QR Code da Página</h5>
                        <p class="text-muted small text-truncate mb-3">{{ qrPageTitle }}</p>

                        <div class="bg-light p-3 rounded-4 d-inline-block shadow-sm mb-3 border">
                            <img v-if="qrCodeUrl" :src="qrCodeUrl" alt="QR Code da Página" class="img-fluid rounded"
                                style="width: 180px; height: 180px;" />
                        </div>

                        <div class="d-grid gap-2">
                            <button class="btn btn-pmg-light btn-sm fw-medium py-2" @click="copyLinkFromModal">
                                <i class="bi bi-clipboard me-1"></i> Copiar Link Público
                            </button>
                            <button class="btn btn-pmg-primary btn-sm fw-medium py-2" @click="downloadQrCode">
                                <i class="bi bi-download me-1"></i> Baixar QR Code (PNG)
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- Modal Confirmação de Exclusão Única -->
        <div class="modal fade" id="confirmDeletePageModal" tabindex="-1" aria-hidden="true">
            <div class="modal-dialog modal-sm modal-dialog-centered">
                <div class="modal-content border-0 shadow-lg rounded-4 text-center p-4">
                    <div class="modal-body p-0">
                        <div class="text-danger mb-3">
                            <i class="bi bi-exclamation-triangle-fill fs-1"></i>
                        </div>
                        <h4 class="fw-bold mb-3">Tem certeza?</h4>
                        <p class="text-muted mb-4 text-small">Excluir "{{ pageTitleToDelete }}"? Esta ação removerá a
                            página e suas seções permanentemente.</p>

                        <div class="d-flex gap-2 justify-content-center">
                            <button class="btn btn-pmg-light px-4 rounded-3" data-bs-dismiss="modal">Cancelar</button>
                            <button class="btn btn-danger px-4 rounded-3" @click="executeDeletePage">Confirmar</button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
    <!-- MODAL DE CONFIRMAÇÃO DE EXCLUSÃO (ÚNICA OU EM MASSA) -->
<transition name="fade">
    <div v-if="showDeleteModal" class="delete-modal-overlay d-flex align-items-center justify-content-center" @click.self="closeDeleteModal">
        <div class="delete-modal-content bg-white rounded-4 shadow-lg p-4 text-center" style="max-width: 440px; width: 90%;">
            
            <!-- Ícone de Alerta -->
            <div class="mx-auto mb-3 d-flex align-items-center justify-content-center rounded-circle bg-danger-subtle text-danger" style="width: 64px; height: 64px;">
                <i class="bi bi-trash3-fill fs-2"></i>
            </div>

            <!-- Título Dinâmico -->
            <h5 class="fw-bold text-dark mb-2">
                {{ isBulkDelete ? `Excluir ${selectedIds.length} páginas?` : 'Excluir Landing Page?' }}
            </h5>

            <!-- Mensagem Dinâmica -->
            <p v-if="isBulkDelete" class="text-muted small mb-4">
                Você está prestes a excluir <strong class="text-dark">{{ selectedIds.length }} Landing Pages selecionadas</strong>. Esta ação é irreversível e os links deixarão de funcionar imediatamente.
            </p>
            <p v-else class="text-muted small mb-4">
                Você está prestes a excluir a página <strong class="text-dark">"{{ pageToDelete?.title }}"</strong>. Esta ação é irreversível e o link deixará de funcionar imediatamente.
            </p>

            <!-- Botões de Ação -->
            <div class="d-flex justify-content-center gap-2">
                <button class="btn btn-light border fw-medium px-4 py-2 rounded-pill" 
                    @click="closeDeleteModal" 
                    :disabled="isDeleting">
                    Cancelar
                </button>
                
                <button class="btn btn-danger fw-bold px-4 py-2 rounded-pill shadow-sm d-inline-flex align-items-center justify-content-center" 
                    @click="executeDeletePage" 
                    :disabled="isDeleting">
                    
                    <span v-if="isDeleting">
                        <span class="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                        Excluindo...
                    </span>
                    <span v-else>
                        <i class="bi bi-trash me-1"></i> {{ isBulkDelete ? 'Excluir Selecionadas' : 'Sim, Excluir' }}
                    </span>
                </button>
            </div>

        </div>
    </div>
</transition>
</template>

<script>
import api from '@/services/api';

export default {
    data() {
        return {
            pages: [],
            alerts: [],
            selectedIds: [],
            filter: { title: '' },

            totalItems: 0,
            currentPage: 1,
            lastPage: 1,
            loading: false,
            hasMore: false,
            showFilter: false,

            qrCodeUrl: '',
            qrPageTitle: '',
            activeSlug: '',

            pageToDeleteId: null,
            pageTitleToDelete: '',
            showDeleteModal: false,
            pageToDelete: null,
            isDeleting: false,
            isBulkDelete: false,
        }
    },
    computed: {
        isAllSelected() {
            return this.pages.length > 0 && this.pages.every(i => this.selectedIds.includes(i.id));
        }
    },
    mounted() {
        this.fetchPages();
    },
    methods: {
        formatTemplateName(template) {
            const map = { layout_1: 'Moderno', layout_2: 'Clean', layout_3: 'Escuro', layout_4: 'Corporativo' };
            return map[template] || template;
        },

        async fetchPages(page = 1, append = false) {
            this.loading = true;
            try {
                const params = {
                    page: page,
                    search: this.filter.title
                };

                // Atualizado para usar o prefixo /client-jwt
                const response = await api.get('/client-jwt/landing-pages', { params });
                const payload = response.data;
                const items = Array.isArray(payload) ? payload : (payload.data || []);
                const paginationData = payload.meta || payload;
                const { current_page, last_page, total } = paginationData;

                if (append) {
                    this.pages = [...this.pages, ...items];
                } else {
                    this.pages = items;
                }

                this.currentPage = current_page || 1;
                this.lastPage = last_page || 1;
                this.totalItems = total || items.length;
                this.hasMore = this.currentPage < this.lastPage;

            } catch (error) {
                this.showAlert('Falha ao carregar as Landing Pages.', 'error');
                console.error("Erro na API:", error);
            } finally {
                this.loading = false;
            }
        },

        loadMore() {
            if (this.hasMore && !this.loading) {
                this.fetchPages(this.currentPage + 1, true);
            }
        },

        applyFilter() {
            this.showFilter = false;
            this.selectedIds = [];
            this.fetchPages(1, false);
        },

        getPublicUrl(slug) {
            const baseUrl = window.location.origin;
            return `${baseUrl}/lp/${slug}`;
        },

        showAlert(message, type = 'success') {
            const id = Date.now();
            let icon = 'bi-check-circle-fill';
            if (type === 'danger') icon = 'bi-exclamation-circle-fill';
            if (type === 'error') icon = 'bi-x-circle-fill';

            this.alerts.push({ id, message, type, icon });
            setTimeout(() => this.removeAlert(id), type === 'error' ? 6000 : 4000);
        },

        removeAlert(id) {
            this.alerts = this.alerts.filter(a => a.id !== id);
        },

        toggleAll(e) {
            if (e.target.checked) this.selectedIds = this.pages.map(c => c.id);
            else this.selectedIds = [];
        },

        // Atualizado para a rota de edição correta
        editPage(page) {
            this.$router.push(`/dashboard/edit-landing-page/${page.id}`);
        },

        openQrModal(page) {
            this.qrPageTitle = page.title;
            this.activeSlug = page.slug;

            const url = this.getPublicUrl(page.slug);
            this.qrCodeUrl = `https://api.qrserver.com/v1/create-qr-code/?size=250x250&data=${encodeURIComponent(url)}`;

            const modalElement = document.getElementById('qrCodeModal');
            if (modalElement) {
                const modal = new window.bootstrap.Modal(modalElement);
                modal.show();
            }
        },

        copyLinkFromModal() {
            const url = this.getPublicUrl(this.activeSlug);
            navigator.clipboard.writeText(url)
                .then(() => {
                    this.showAlert('Link copiado para a área de transferência!');
                })
                .catch(() => {
                    this.showAlert('Erro ao copiar link.', 'error');
                });
        },

        async downloadQrCode() {
            try {
                const response = await fetch(this.qrCodeUrl);
                const blob = await response.blob();
                const link = document.createElement('a');

                link.href = window.URL.createObjectURL(blob);
                const safeSlug = this.activeSlug ? this.activeSlug.replace(/[^a-z0-9]/gi, '_').toLowerCase() : 'qrcode';
                link.download = `qrcode-${safeSlug}.png`;

                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);

                window.URL.revokeObjectURL(link.href);
                this.showAlert('QR Code baixado com sucesso!');
            } catch (error) {
                console.error('Erro ao baixar QR Code:', error);
                this.showAlert('Não foi possível baixar o QR Code.', 'error');
            }
        },

      

        // Função adicionada para tratar a exclusão em massa selecionada na interface
        confirmDelete(count, isBulk) {
            if (isBulk && this.selectedIds.length > 0) {
                if (confirm(`Tem certeza que deseja excluir ${this.selectedIds.length} páginas selecionadas?`)) {
                    this.executeBulkDelete();
                }
            }
        },

        async executeBulkDelete() {
            try {
                const deletePromises = this.selectedIds.map(id =>
                    api.delete(`/client-jwt/landing-pages/${id}`)
                );

                await Promise.all(deletePromises);

                this.showAlert('Páginas excluídas com sucesso!');
                this.selectedIds = [];
                this.fetchPages(1, false);
            } catch (error) {
                console.error(error);
                this.showAlert('Erro ao excluir páginas em massa.', 'error');
            }
        },
        formatCurrency(value) {
            if (value === undefined || value === null) return '0,00';
            return parseFloat(value).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
        },
      

        confirmDeletePage(item) {
        this.pageToDelete = item;
        this.isBulkDelete = false;
        this.showDeleteModal = true;
    },

    // 2. Confirmação de exclusão em massa
    confirmBulkDelete() {
        if (this.selectedIds.length === 0) return;
        this.pageToDelete = null;
        this.isBulkDelete = true;
        this.showDeleteModal = true;
    },

    // 3. Fecha o modal e reseta as flags
    closeDeleteModal() {
        if (this.isDeleting) return;
        this.showDeleteModal = false;
        this.pageToDelete = null;
        this.isBulkDelete = false;
    },

    // 4. Executa a exclusão (Individual ou em Massa)
    async executeDeletePage() {
        this.isDeleting = true;

        try {
            if (this.isBulkDelete) {
                // CHAMADA EM MASSA
                await api.post('/client-jwt/landing-pages/bulk-delete', {
                    ids: this.selectedIds
                });

                // Remove localmente todas as páginas que estavam selecionadas
                this.pages = this.pages.filter(p => !this.selectedIds.includes(p.id));
                this.selectedIds = []; // Limpa a seleção
                this.showAlert('Páginas selecionadas excluídas com sucesso!', 'success');

            } else {
                // CHAMADA INDIVIDUAL
                if (!this.pageToDelete) return;
                
                await api.delete(`/client-jwt/landing-pages/${this.pageToDelete.id}`);

                // Remove a página localmente da lista
                this.pages = this.pages.filter(p => p.id !== this.pageToDelete.id); 
                // Se por acaso ela estivesse selecionada no checkbox, limpa também 
                this.selectedIds = this.selectedIds.filter(id => id !== this.pageToDelete.id);
                
                this.showAlert('Landing Page excluída com sucesso!', 'success');
            }

           this.isDeleting = false;
            this.showDeleteModal = false;
            this.pageToDelete = null;
            this.isBulkDelete = false;

        } catch (error) {
            console.error("Erro ao excluir páginas:", error);
            const msg = error.response?.data?.message || 'Erro ao tentar excluir.';
            this.showAlert(msg, 'danger');
        } finally {
            this.isDeleting = false;
        }
    },
        
    }
}
</script>

<style scoped>
/* Estilos visuais mantidos idênticos ao padrão do RMR Sistemas */
.toast {
    min-width: 300px;
    border-radius: 12px;
    background-color: white !important;
}

.bg-success-subtle {
    background-color: #ecfdf5 !important;
    color: #059669 !important;
    border-left: 5px solid #059669 !important;
}

.bg-danger-subtle {
    background-color: #fef2f2 !important;
    color: #dc2626 !important;
    border-left: 5px solid #dc2626 !important;
}

.bg-error-subtle {
    background-color: #fef2f2 !important;
    color: #991b1b !important;
    border-left: 5px solid #ef4444 !important;
}

.main-content {
    background-color: #f8fafc;
    min-height: 100vh;
    padding: 2rem;
    font-family: 'Inter', sans-serif;
}

.card {
    border-radius: 16px;
    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.02);
}

.btn-pmg-primary {
    background: #561569;
    color: #fff;
    border: none;
    border-radius: 10px;
    padding: 10px 20px;
    font-weight: 500;
}

.btn-pmg-primary:hover {
    background: #451054;
    color: #fff;
}

.btn-pmg-light {
    background: #fff;
    border: 1px solid #e2e8f0;
    border-radius: 10px;
    color: #64748b;
    padding: 10px 20px;
}

.btn-pmg-danger {
    background: #fee2e2;
    color: #b91c1c;
    border: 1px solid #fecaca;
    border-radius: 10px;
    font-weight: 600;
}

.form-control-pmg {
    border-radius: 10px;
    border: 1px solid #e2e8f0;
    padding: 12px;
}

.table-pmg thead th {
    background: #f8fafc;
    color: #64748b;
    font-size: 0.7rem;
    text-transform: uppercase;
    letter-spacing: 1px;
    padding: 1.2rem;
    border-bottom: 1px solid #edf2f7;
}

.table-pmg tbody td {
    padding: 1.2rem;
    border-bottom: 1px solid #f1f5f9;
}

.text-truncate-custom {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.slide-fade-enter-active,
.slide-fade-leave-active {
    transition: all 0.4s ease;
}

.slide-fade-enter-from {
    transform: translateX(100px);
    opacity: 0;
}

.slide-fade-leave-to {
    opacity: 0;
    transform: scale(0.9);
}

.dropdown-item {
    font-size: 0.9rem;
    color: #334155;
    transition: all 0.2s ease;
}

.dropdown-item:hover {
    background-color: #f8fafc;
    color: #1e293b;
}

.dropdown-item.text-danger:hover {
    background-color: #fff1f2 !important;
    color: #dc2626 !important;
}

.breadcrumb::-webkit-scrollbar {
    display: none;
}

.breadcrumb-item .nav-link {
    display: inline;
    padding: 0;
}

.dropdown-menu {
    z-index: 1050 !important;
}

.table-responsive {
    overflow: visible !important;
}

.dropdown-menu {
    z-index: 1060 !important;
}
/* OVERLAY ESCURO E ANIMAÇÃO DO MODAL */
.delete-modal-overlay {
    position: fixed;
    top: 0;
    left: 0;
    width: 100vw;
    height: 100vh;
    background-color: rgba(15, 23, 42, 0.6); /* Backdrop escuro elegante */
    backdrop-filter: blur(4px); /* Efeito de desfoque no fundo */
    z-index: 9999;
}

.delete-modal-content {
    animation: scaleUp 0.2s ease-out;
}

@keyframes scaleUp {
    from {
        opacity: 0;
        transform: scale(0.95);
    }
    to {
        opacity: 1;
        transform: scale(1);
    }
}

.fade-enter-active, .fade-leave-active {
    transition: opacity 0.2s ease;
}
.fade-enter-from, .fade-leave-to {
    opacity: 0;
}
</style>